import { Alert, AlertDescription } from "@veritrellis/ui";
import { PlanFeatureAlert } from "../billing/PlanFeatureAlert.js";
import { PolicyModeCards } from "./PolicyModeCards.js";
import { PolicyRuleLadder } from "./PolicyRuleLadder.js";
import { emptyRule } from "./builderTypes.js";
import { validateBuilderState } from "./validation.js";
import type { PlanFeatures } from "../../hooks/useWorkspaceBilling.js";
import type { BuilderState } from "./builderTypes.js";

type Props = {
  state: BuilderState;
  onChange: (next: BuilderState) => void;
  workspaceId: string;
  environment: "sandbox" | "production";
  planFeatures: PlanFeatures;
  billingLoaded?: boolean;
  disabled?: boolean;
  matchedRuleIndex?: number | null;
};

export const PolicyBuilderForm = ({
  state,
  onChange,
  workspaceId,
  environment,
  planFeatures,
  billingLoaded = true,
  disabled = false,
  matchedRuleIndex = null
}: Props) => {
  const validationResults = validateBuilderState(state, planFeatures);
  const errors = validationResults.filter((v) => v.severity === "error");
  const warnings = validationResults.filter((v) => v.severity === "warning");

  const handleModeChange = (mode: BuilderState["mode"]) => {
    if (mode === "conditional" && state.rules.length === 0) {
      onChange({
        ...state,
        mode,
        rules: [emptyRule(crypto.randomUUID())]
      });
      return;
    }
    onChange({ ...state, mode });
  };

  return (
    <div className="flex flex-col gap-4">
      <PolicyModeCards value={state.mode} onChange={handleModeChange} disabled={disabled} />

      {state.mode === "conditional" ? (
        <>
          <PlanFeatureAlert
            feature="feature_escalation_rules"
            enabled={planFeatures.escalationRules}
            billingLoaded={billingLoaded}
          />
          <PolicyRuleLadder
            state={state}
            onChange={onChange}
            workspaceId={workspaceId}
            environment={environment}
            planFeatures={planFeatures}
            billingLoaded={billingLoaded}
            disabled={disabled}
            matchedRuleIndex={matchedRuleIndex}
          />
        </>
      ) : null}

      {errors.map((e, i) => (
        <Alert key={i} variant="destructive">
          <AlertDescription>{e.message}</AlertDescription>
        </Alert>
      ))}

      {warnings.map((w, i) => (
        <Alert key={i} className="border-amber-200 bg-amber-50 text-amber-900">
          <AlertDescription>{w.message}</AlertDescription>
        </Alert>
      ))}

      {validationResults.length === 0 && state.mode === "conditional" && state.rules.length > 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "#16a34a" }}>Policy looks valid.</p>
      ) : null}
    </div>
  );
};
